import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import type { ComponentLike, WithBoundArgs } from '@glint/template';
import type { FieldValue } from './form';

/** Control size for standard radio options; card options use the shared tile recipe. */
export type RadioGroupSize = 'sm' | 'md' | 'lg';

export interface RadioOption {
  /** Unique key for the option */
  key: string;
  /** Display label */
  label: string;
  /** Optional description shown below the label */
  description?: string;
  /** Decorative icon displayed above the label in card appearance. */
  icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Whether this option is disabled */
  disabled?: boolean;
  /** data-test-id for per-option test targeting */
  dataTestId?: string;
  /** aria-label override for the radio input */
  ariaLabel?: string;
}

interface RadioSignature {
  Args: {
    value: FieldValue | boolean;
    label: string;
    disabled?: boolean;
    /** Shared name for mutual exclusion (auto-provided by RadioGroup) */
    name?: string;
    /** Parent's selected value for checked state (auto-provided by RadioGroup) */
    groupValue?: FieldValue | boolean;
  };
  Element: HTMLLabelElement;
}

class Radio extends Component<RadioSignature> {
  private get isChecked(): boolean {
    return String(this.args.groupValue) === String(this.args.value);
  }

  <template>
    <label
      data-test-radio-option
      class="tio-row-tight cursor-pointer select-none
        {{if @disabled 'opacity-50 cursor-not-allowed'}}"
      ...attributes
    >
      <input
        type="radio"
        name={{@name}}
        value={{@value}}
        checked={{this.isChecked}}
        disabled={{@disabled}}
        class="tio-radio-native"
      />
      <span class="tio-text-sm-medium tio-fg-secondary">{{@label}}</span>
    </label>
  </template>
}

interface RadioGroupSignature {
  Args: {
    /**
     * Stable identity for automation: emitted as `data-test-hook` on this
     * component's root, alongside its `data-test-*` kind markers.
     *
     * The call site says WHICH instance this is; the component already says
     * what KIND it is. Together they give a key that survives a re-render,
     * which `id` does not — 74% of ids in these apps are Ember-generated
     * (`…-ember42`) and change whenever the component tree is rebuilt. One
     * hook covers the whole subtree: a descendant control is identified as
     * its nearest ancestor hook plus its own role.
     *
     * Consumed by the ux-audit's per-element interaction ledger. See
     * tuitionio/tio-front-end#10057.
     *
     * @example <RadioGroup @testHook="pslf__employment-type" @options={{this.types}} />
     */
    testHook?: string;
    /** Currently selected option key (accepts boolean for yes/no groups) */
    value?: FieldValue | boolean;
    /** Called when an option is selected */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- permissive callback contract: callers pass handlers typed with varied value types
    onChange?: (value: any) => void;
    /** Label displayed above the group */
    label?: string;
    /** Hint text displayed below the label */
    hint?: string;
    /** Validation error messages */
    errors?: string[];
    /** Show required indicator on label */
    isRequired?: boolean;
    /** Disable the entire group */
    isDisabled?: boolean;
    /** Layout direction @default "vertical" */
    orientation?: 'vertical' | 'horizontal';
    /** Options to render (omit for block mode) */
    options?: RadioOption[];
    /** Size variant @default "md" */
    size?: RadioGroupSize;
    /** Card appearance for options mode; native radios retain mutual exclusion and arrow-key navigation. */
    appearance?: 'standard' | 'cards';
  };
  Blocks: {
    default: [WithBoundArgs<typeof Radio, 'name' | 'groupValue'>];
  };
  Element: HTMLFieldSetElement;
}

const SIZE_CLASSES: Record<RadioGroupSize, { radio: string; label: string }> = {
  sm: { radio: 'w-3.5 h-3.5', label: 'text-sm' },
  md: { radio: 'w-4 h-4', label: 'text-sm' },
  lg: { radio: 'w-5 h-5', label: 'text-base' },
};

/**
 * Reimagined RadioGroup — a zero-dependency radio button group component.
 *
 * Renders a group of radio buttons from an `@options` array.
 * Uses violet accent for the selected state, matching other reimagined components.
 *
 * @example Basic usage
 * ```gts
 * import RadioGroup from 'tio-ui/components/radio-group';
 *
 * <RadioGroup
 *   @label="Employment Type"
 *   @options={{this.employmentOptions}}
 *   @value={{this.selectedType}}
 *   @onChange={{this.setType}}
 * />
 * ```
 *
 * @example Horizontal with errors
 * ```gts
 * <RadioGroup
 *   @label="Priority"
 *   @orientation="horizontal"
 *   @options={{array
 *     (hash key="low" label="Low")
 *     (hash key="medium" label="Medium")
 *     (hash key="high" label="High")
 *   }}
 *   @value={{this.priority}}
 *   @onChange={{this.setPriority}}
 *   @errors={{array "Priority is required"}}
 *   @isRequired={{true}}
 * />
 * ```
 */
// String-coerced equality for comparing boolean/string values in templates
const looseEq = (a: unknown, b: unknown): boolean => String(a) === String(b);

export default class RadioGroup extends Component<RadioGroupSignature> {
  private _groupName = `reimagined-radio-${guidFor(this)}`;

  private get size(): RadioGroupSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  private get isHorizontal(): boolean {
    return this.args.orientation === 'horizontal';
  }

  /** Emits one selected value per native change; block-mode boolean values are coerced. */
  handleChange = (event: Event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;
    let value: FieldValue | boolean = input.value;
    // In block mode (no @options), coerce "true"/"false" strings to boolean
    // since block mode is typically used for boolean yes/no patterns.
    // Options mode always uses string keys, so no coercion needed.
    if (!this.args.options) {
      if (input.value === 'true') value = true;
      else if (input.value === 'false') value = false;
    }
    this.args.onChange?.(value);
  };

  <template>
    <fieldset
      data-test-radio-group
      data-test-hook={{@testHook}}
      class="tio-col-tight {{if @isDisabled 'opacity-50'}}"
      disabled={{@isDisabled}}
      ...attributes
      {{on "change" this.handleChange}}
    >
      {{#if @label}}
        <legend data-test-radio-group-label class="tio-text-sm-medium tio-fg-secondary mb-1">
          {{@label}}
          {{#if @isRequired}}
            <span class="tio-fg-danger ml-0.5" aria-hidden="true">*</span>
          {{/if}}
        </legend>
      {{/if}}

      {{#if @hint}}
        <p data-test-radio-group-hint class="text-xs tio-fg-muted -mt-1 mb-1">{{@hint}}</p>
      {{/if}}

      {{#if @options}}
        <div
          class="{{if
              (eq @appearance 'cards')
              'tio-radio-cards'
              (if this.isHorizontal 'tio-wrap-flush gap-x-6 gap-y-2' 'tio-col-tight')
            }}"
        >
          {{#each @options as |option|}}
            {{#if (eq @appearance "cards")}}
              <label
                data-test-radio-option
                data-test-id={{option.dataTestId}}
                class="tio-radio-card"
              >
                <input
                  type="radio"
                  name={{this._groupName}}
                  value={{option.key}}
                  checked={{looseEq @value option.key}}
                  disabled={{option.disabled}}
                  class="sr-only"
                />
                <span
                  class="tio-card tio-card--radius-nested tio-card--elevation-none tio-card--padding-sm tio-radio-card__surface"
                >
                  {{#if option.icon}}
                    {{#let option.icon as |Icon|}}
                      <Icon @size={{28}} aria-hidden="true" />
                    {{/let}}
                  {{/if}}
                  <span class="tio-type-label">{{option.label}}</span>
                  {{#if option.description}}
                    <span class="tio-type-caption tio-fg-muted">{{option.description}}</span>
                  {{/if}}
                </span>
              </label>
            {{else}}
              <label
                data-test-radio-option
                data-test-id={{option.dataTestId}}
                class="flex items-start gap-2.5 cursor-pointer select-none
                  {{if option.disabled 'opacity-50 cursor-not-allowed'}}"
              >
                <span class="relative shrink-0 mt-0.5">
                  <input
                    type="radio"
                    name={{this._groupName}}
                    value={{option.key}}
                    checked={{looseEq @value option.key}}
                    disabled={{option.disabled}}
                    class="peer sr-only"
                  />
                  <span
                    class="tio-radio
                      {{this.sizeClasses.radio}}
                      {{if (looseEq @value option.key) 'tio-radio--checked'}}
                      {{if this.hasErrors 'tio-radio--error'}}"
                  >
                    {{#if (looseEq @value option.key)}}
                      <span class="tio-row-center absolute inset-0">
                        <span class="tio-radio__dot"></span>
                      </span>
                    {{/if}}
                  </span>
                </span>

                <div class="tio-col-flush">
                  <span class="{{this.sizeClasses.label}} font-medium tio-fg-secondary">
                    {{option.label}}
                  </span>
                  {{#if option.description}}
                    <span class="text-xs tio-fg-muted mt-0.5">{{option.description}}</span>
                  {{/if}}
                </div>
              </label>
            {{/if}}
          {{/each}}
        </div>
      {{else}}
        {{! Block mode: yield Radio with name + groupValue curried from parent }}
        {{yield (component Radio name=this._groupName groupValue=@value)}}
      {{/if}}

      {{#if this.hasErrors}}
        <div data-test-radio-group-errors class="space-y-0.5">
          {{#each @errors as |error|}}
            <p class="text-xs tio-fg-danger" role="alert">{{error}}</p>
          {{/each}}
        </div>
      {{/if}}
    </fieldset>
  </template>
}

export type { RadioGroupSignature };
